import { useEffect, useMemo, useState } from 'react';
import Button from '@/ui/_lib/Button';

type Platform = 'android' | 'ios' | 'none';

const DISMISS_KEY = 'pwaInstallDismissedAt';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isIOS() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  return /iphone|ipad|ipod/i.test(ua);
}

function isStandalone() {
  return (
    (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
    (typeof navigator !== 'undefined' && (navigator as any).standalone === true)
  );
}

function dismissedRecently() {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    return ts ? Date.now() - Number(ts) < DISMISS_TTL_MS : false;
  } catch {
    return false;
  }
}

function setDismissedNow() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* no-op */
  }
}

export function useInstallPrompt() {
  const [bipEvent, setBipEvent] = useState<any>(null);
  const [platform, setPlatform] = useState<Platform>('none');
  const [installed, setInstalled] = useState<boolean>(isStandalone());
  const [dismissed, setDismissed] = useState<boolean>(dismissedRecently());

  useEffect(() => {
    if (installed) return undefined;
    if (isIOS()) setPlatform('ios');

    const onBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setBipEvent(e);
      setPlatform('android');
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setBipEvent(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, [installed]);

  const promptInstall = async () => {
    if (!bipEvent) return { outcome: 'dismissed' } as const;
    bipEvent.prompt();
    const choiceResult = await bipEvent.userChoice;
    setBipEvent(null);
    return choiceResult;
  };

  const canInstall = useMemo(() => {
    if (installed || dismissed) return false;
    if (platform === 'android') return !!bipEvent;
    if (platform === 'ios') return true;
    return false;
  }, [platform, bipEvent, installed, dismissed]);

  const dismiss = () => {
    setDismissedNow();
    setDismissed(true);
  };

  return { platform, canInstall, promptInstall, installed, dismissed, dismiss };
}

export function InstallBanner() {
  const { platform, canInstall, promptInstall, dismiss } = useInstallPrompt();
  const [busy, setBusy] = useState(false);
  if (!canInstall) return null;

  return (
    <div
      className="fixed bottom-3 left-0 right-0 z-[2147483000] flex justify-center px-3"
      role="dialog"
      aria-live="polite"
    >
      <div className="flex w-full max-w-[32rem] items-center gap-3 rounded-2xl bg-white px-4 py-3 shadow-xl">
        <img src="/apple-touch-icon.png" alt="Linkozi" className="h-7 w-7 rounded-md" />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-gray-900">Install Linkozi</div>
          {platform === 'android' ? (
            <p className="text-xs text-gray-700">Get quick access by installing the app to your home screen.</p>
          ) : (
            <p className="text-xs text-gray-700">On iPhone/iPad, tap Share, then “Add to Home Screen” to install.</p>
          )}
        </div>
        {platform === 'android' ? (
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="small"
              className={`text-xs font-medium ${busy ? 'pointer-events-none opacity-50' : ''}`}
              onClick={async () => {
                if (busy) return;
                setBusy(true);
                try {
                  await promptInstall();
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Installing…' : 'Install'}
            </Button>
            <Button variant="secondary" size="small" className="text-xs font-medium" onClick={dismiss}>
              Not now
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="primary" size="small" className="text-xs font-medium" onClick={dismiss}>
              OK
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
